<template>
  <div class="goal-view">
    <!-- Carte d'objectif actif -->
    <section v-if="currentGoal && !isEditing" :class="['goal-card', { 'goal-completed': isGoalCompleted }]">
      <!-- Bannière festive si objectif atteint -->
      <div v-if="isGoalCompleted" class="celebration-banner">
        <div class="confetti"></div>
        <div class="celebration-content">
          <span class="celebration-emoji">🎉</span>
          <span class="celebration-text">OBJECTIF ATTEINT !</span>
          <span class="celebration-emoji">🏆</span>
        </div>
        <div class="confetti"></div>
      </div>

      <div class="goal-header">
        <h2>{{ isGoalCompleted ? '🏆 Objectif atteint !' : '🎯 Objectif en cours' }}</h2>
        <div class="goal-actions">
          <button @click="startEdit" class="edit-goal-btn" title="Modifier l'objectif">
            ✏️
          </button>
          <button @click="showArchiveModal = true" class="archive-goal-btn" :title="isGoalCompleted ? 'Archiver et célébrer' : 'Archiver l\'objectif'">
            📦
          </button>
          <button @click="deleteGoal" class="delete-goal-btn" title="Supprimer l'objectif">
            🗑️
          </button>
        </div>
      </div>

      <div class="goal-info">
        <div class="goal-metric">
          <span class="label">Métrique :</span>
          <span class="value">{{ getMetricLabel(currentGoal.metric) }}</span>
        </div>
        <div class="goal-target">
          <span class="label">Objectif :</span>
          <span class="value">{{ currentGoal.targetValue }} {{ getMetricUnit(currentGoal.metric) }}</span>
        </div>
        <div class="goal-start">
          <span class="label">Valeur de départ :</span>
          <span class="value">{{ startValueDisplay }} {{ getMetricUnit(currentGoal.metric) }}</span>
        </div>
        <div class="goal-current">
          <span class="label">Valeur actuelle :</span>
          <span class="value">{{ currentValue }} {{ getMetricUnit(currentGoal.metric) }}</span>
        </div>
        <div class="goal-diff">
          <span class="label">Différence :</span>
          <span :class="['value', diffClass]">{{ difference }} {{ getMetricUnit(currentGoal.metric) }}</span>
        </div>
        <div class="goal-period">
          <span class="label">Période :</span>
          <span class="value">{{ formatDateRange(currentGoal.startDate, currentGoal.endDate) }}</span>
        </div>
        <div class="goal-days">
          <span class="label">Durée :</span>
          <span class="value">{{ calculateDuration(currentGoal.startDate, currentGoal.endDate) }} jours</span>
        </div>
      </div>

      <div class="progress-section">
        <div :class="['progress-bar-container', { 'completed': isGoalCompleted }]">
          <div :class="['progress-bar', { 'completed': isGoalCompleted }]" :style="{ width: progressPercent + '%' }"></div>
        </div>
        <p :class="['progress-text', { 'completed': isGoalCompleted }]">
          {{ isGoalCompleted ? '🎊 100% - Objectif atteint ! 🎊' : progressPercent + '% de progression' }}
        </p>
      </div>

      <!-- Bouton d'archivage mis en avant si objectif atteint -->
      <div v-if="isGoalCompleted" class="archive-prompt">
        <button @click="showArchiveModal = true" class="archive-celebration-btn">
          🎉 Archiver et célébrer cet objectif !
        </button>
      </div>
    </section>

    <!-- Formulaire de modification d'objectif -->
    <section v-if="currentGoal && isEditing" class="form-section editing">
      <div class="goal-header">
        <h2>✏️ Modifier l'objectif</h2>
        <button @click="cancelEdit" class="cancel-edit-btn" title="Annuler">
          ✕
        </button>
      </div>

      <form @submit.prevent="updateGoal" class="goal-form">
        <div class="form-group">
          <label for="editMetric">Métrique à suivre :</label>
          <select v-model="editGoal.metric" id="editMetric" required>
            <option value="">-- Sélectionner une métrique --</option>
            <option value="poids">Poids (kg)</option>
            <option value="imc">IMC</option>
            <option value="graisse_corporelle">Graisse corporelle (%)</option>
            <option value="taux_musculaire">Taux musculaire (%)</option>
            <option value="graisse_viscerale">Graisse viscérale</option>
            <option value="eau_corporelle">Eau corporelle (%)</option>
            <option value="muscle_squelettique">Muscle squelettique (%)</option>
            <option value="masse_musculaire">Masse musculaire (kg)</option>
          </select>
        </div>

        <div class="form-group">
          <label for="editTargetValue">Valeur cible :</label>
          <input
            type="number"
            step="0.1"
            v-model.number="editGoal.targetValue"
            id="editTargetValue"
            required
            placeholder="Ex: 75"
          />
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="editStartDate">Date de début :</label>
            <input
              type="date"
              v-model="editGoal.startDate"
              id="editStartDate"
              required
            />
          </div>

          <div class="form-group">
            <label for="editEndDate">Date de fin :</label>
            <input
              type="date"
              v-model="editGoal.endDate"
              id="editEndDate"
              required
              :min="editGoal.startDate"
            />
          </div>
        </div>

        <div class="form-actions">
          <button type="submit" class="submit-btn">
            💾 Enregistrer les modifications
          </button>
          <button type="button" @click="cancelEdit" class="cancel-btn">
            Annuler
          </button>
        </div>
      </form>
    </section>

    <!-- Graphique d'évolution -->
    <section v-if="currentGoal" class="chart-section">
      <h2>📈 Évolution de la période</h2>
      <div class="chart-wrapper">
        <div v-if="!isChartReady" class="chart-loading-overlay">
          <p>Chargement du graphique...</p>
        </div>
        <div class="chart-container">
          <canvas ref="chartCanvas"></canvas>
        </div>
      </div>
    </section>

    <!-- Formulaire de création d'objectif -->
    <section v-if="!currentGoal" class="form-section">
      <h2>🎯 Créer un nouvel objectif mensuel</h2>

      <form @submit.prevent="createGoal" class="goal-form">
        <div class="form-group">
          <label for="metric">Métrique à suivre :</label>
          <select v-model="newGoal.metric" id="metric" required>
            <option value="">-- Sélectionner une métrique --</option>
            <option value="poids">Poids (kg)</option>
            <option value="imc">IMC</option>
            <option value="graisse_corporelle">Graisse corporelle (%)</option>
            <option value="taux_musculaire">Taux musculaire (%)</option>
            <option value="graisse_viscerale">Graisse viscérale</option>
            <option value="eau_corporelle">Eau corporelle (%)</option>
            <option value="muscle_squelettique">Muscle squelettique (%)</option>
            <option value="masse_musculaire">Masse musculaire (kg)</option>
          </select>
        </div>

        <div class="form-group">
          <label for="targetValue">Valeur cible :</label>
          <input
            type="number"
            step="0.1"
            v-model.number="newGoal.targetValue"
            id="targetValue"
            required
            placeholder="Ex: 75"
          />
        </div>

        <div class="form-row">
          <div class="form-group">
            <label for="startDate">Date de début :</label>
            <input
              type="date"
              v-model="newGoal.startDate"
              id="startDate"
              required
            />
          </div>

          <div class="form-group">
            <label for="endDate">Date de fin :</label>
            <input
              type="date"
              v-model="newGoal.endDate"
              id="endDate"
              required
              :min="newGoal.startDate"
            />
          </div>
        </div>

        <button type="submit" class="submit-btn">
          Créer l'objectif
        </button>
      </form>
    </section>

    <!-- Message si aucun objectif -->
    <section v-if="!currentGoal && !showForm" class="empty-state">
      <div class="empty-icon">🎯</div>
      <p>Aucun objectif en cours</p>
      <button @click="showForm = true" class="create-btn">
        Créer un objectif
      </button>
    </section>

    <!-- Modal de confirmation de suppression -->
    <div v-if="showDeleteModal" class="modal-overlay" @click="showDeleteModal = false">
      <div class="modal-content" @click.stop>
        <h3>Confirmer la suppression</h3>
        <p>Voulez-vous vraiment supprimer cet objectif ?</p>
        <div class="modal-actions">
          <button @click="confirmDelete" class="btn-danger">Supprimer</button>
          <button @click="showDeleteModal = false" class="btn-cancel">Annuler</button>
        </div>
      </div>
    </div>

    <!-- Modal de confirmation d'archivage -->
    <div v-if="showArchiveModal" class="modal-overlay" @click="showArchiveModal = false">
      <div class="modal-content archive-modal" @click.stop>
        <div class="archive-modal-header">
          <span class="archive-emoji">📦</span>
          <h3>{{ isGoalCompleted ? 'Félicitations !' : 'Archiver l\'objectif' }}</h3>
        </div>
        <p v-if="isGoalCompleted">
          🎉 Vous avez atteint votre objectif ! Voulez-vous l'archiver pour en créer un nouveau ?
        </p>
        <p v-else>
          Voulez-vous archiver cet objectif ? Il sera conservé dans l'historique.
        </p>
        <div class="modal-actions">
          <button @click="confirmArchive" class="btn-archive">
            {{ isGoalCompleted ? '🏆 Archiver et célébrer' : '📦 Archiver' }}
          </button>
          <button @click="showArchiveModal = false" class="btn-cancel">Annuler</button>
        </div>
      </div>
    </div>

    <!-- Section Historique des objectifs archivés -->
    <section v-if="archivedGoals.length > 0" class="history-section">
      <div class="history-header" @click="showHistory = !showHistory">
        <h2>📦 Historique des objectifs ({{ archivedGoals.length }})</h2>
        <span class="toggle-icon">{{ showHistory ? '▼' : '▶' }}</span>
      </div>

      <div v-if="showHistory" class="history-list">
        <div v-for="goal in archivedGoals" :key="goal.id" class="history-card">
          <div class="history-card-header">
            <span class="history-badge">✅ Atteint</span>
            <span class="history-date">Archivé le {{ formatDate(goal.archivedAt) }}</span>
          </div>
          <div class="history-card-body">
            <div class="history-metric">
              <strong>{{ getMetricLabel(goal.metric) }}</strong>
              <span>Objectif : {{ goal.targetValue }} {{ getMetricUnit(goal.metric) }}</span>
            </div>
            <div class="history-period">
              {{ formatDateRange(goal.startDate, goal.endDate) }}
            </div>
          </div>
          <div class="history-card-actions">
            <button @click="unarchiveGoal(goal.id)" class="unarchive-btn" title="Réactiver cet objectif">
              🔄 Réactiver
            </button>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { Chart, registerables } from 'chart.js'
import { bodyMetricsAPI } from '../services/api.js'

Chart.register(...registerables)

export default {
  name: 'GoalView',
  props: {
    currentUser: {
      type: String,
      required: true
    },
    userData: {
      type: Object,
      required: true
    }
  },
  emits: ['goals-updated'],
  setup(props, { emit }) {
    const chartCanvas = ref(null)
    const chartInstance = ref(null)
    const showDeleteModal = ref(false)
    const showArchiveModal = ref(false)
    const showForm = ref(false)
    const showHistory = ref(false)
    const isChartReady = ref(false)
    const isRendering = ref(false)
    const isEditing = ref(false)

    const newGoal = ref({
      metric: '',
      targetValue: null,
      startDate: new Date().toISOString().slice(0, 10),
      endDate: ''
    })

    const editGoal = ref({
      metric: '',
      targetValue: null,
      startDate: '',
      endDate: ''
    })

    const currentGoal = computed(() => {
      if (!props.userData || !props.userData.data || !props.userData.data.goals) return null
      const goals = props.userData.data.goals
      // Filtrer pour ne garder que les objectifs actifs (non archivés)
      const activeGoals = goals.filter(g => g.status !== 'archived')
      return activeGoals.length > 0 ? activeGoals[activeGoals.length - 1] : null
    })

    const archivedGoals = computed(() => {
      if (!props.userData || !props.userData.data || !props.userData.data.goals) return []
      const goals = props.userData.data.goals
      // Filtrer pour ne garder que les objectifs archivés, triés par date d'archivage (récent en premier)
      return goals
        .filter(g => g.status === 'archived')
        .sort((a, b) => new Date(b.archivedAt) - new Date(a.archivedAt))
    })

    const isGoalCompleted = computed(() => {
      return progressPercent.value >= 100
    })

    const currentValue = computed(() => {
      if (!currentGoal.value || !props.userData?.data?.measurements) return '-'

      const measurements = props.userData.data.measurements
      if (measurements.length === 0) return '-'

      // Trier par date et prendre la plus récente
      const sortedMeasurements = [...measurements].sort((a, b) => {
        const dateA = new Date(a.date.split('/').reverse().join('-') + 'T' + a.time)
        const dateB = new Date(b.date.split('/').reverse().join('-') + 'T' + b.time)
        return dateB - dateA
      })

      const latestMeasurement = sortedMeasurements[0]
      const metricData = latestMeasurement.data[currentGoal.value.metric]
      return metricData?.value || '-'
    })

    const startValueDisplay = computed(() => {
      if (!currentGoal.value || !props.userData?.data?.measurements) return '-'

      // Priorité 1 : valeur sauvegardée dans l'objectif
      if (currentGoal.value.startValue) {
        return currentGoal.value.startValue
      }

      // Priorité 2 : première mesure de la période
      const goalStartDate = currentGoal.value.startDate
      const goalEndDate = currentGoal.value.endDate
      const metric = currentGoal.value.metric

      const periodMeasurements = props.userData.data.measurements
        .filter(m => {
          const measurementDateStr = m.date.split('/').reverse().join('-')
          return measurementDateStr >= goalStartDate && measurementDateStr <= goalEndDate
        })
        .sort((a, b) => {
          const dateA = new Date(a.date.split('/').reverse().join('-') + 'T' + a.time)
          const dateB = new Date(b.date.split('/').reverse().join('-') + 'T' + b.time)
          return dateA - dateB
        })

      if (periodMeasurements.length > 0) {
        return periodMeasurements[0].data[metric]?.value || '-'
      }

      return '-'
    })

    const difference = computed(() => {
      if (currentValue.value === '-' || !currentGoal.value) return '-'
      const diff = currentGoal.value.targetValue - currentValue.value
      return diff.toFixed(1)
    })

    const diffClass = computed(() => {
      if (difference.value === '-') return ''
      const diff = parseFloat(difference.value)
      if (diff > 0) return 'positive'
      if (diff < 0) return 'negative'
      return 'neutral'
    })

    const progressPercent = computed(() => {
      if (!currentGoal.value || currentValue.value === '-') return 0

      const target = currentGoal.value.targetValue
      const current = currentValue.value

      // Déterminer la valeur de départ
      let start = currentGoal.value.startValue

      // Si pas de startValue, prendre la première mesure de la période
      if (!start && props.userData?.data?.measurements) {
        const goalStartDate = currentGoal.value.startDate
        const goalEndDate = currentGoal.value.endDate
        const metric = currentGoal.value.metric

        const periodMeasurements = props.userData.data.measurements
          .filter(m => {
            const measurementDateStr = m.date.split('/').reverse().join('-')
            return measurementDateStr >= goalStartDate && measurementDateStr <= goalEndDate
          })
          .sort((a, b) => {
            const dateA = new Date(a.date.split('/').reverse().join('-') + 'T' + a.time)
            const dateB = new Date(b.date.split('/').reverse().join('-') + 'T' + b.time)
            return dateA - dateB
          })

        if (periodMeasurements.length > 0) {
          start = periodMeasurements[0].data[metric]?.value
        }
      }

      // Si toujours pas de valeur de départ, utiliser la valeur actuelle
      if (!start) {
        start = current
      }

      if (start === target) return 100

      const progress = ((start - current) / (start - target)) * 100
      return Math.max(0, Math.min(100, Math.round(progress)))
    })

    const getMetricLabel = (metric) => {
      const labels = {
        poids: 'Poids',
        imc: 'IMC',
        graisse_corporelle: 'Graisse corporelle',
        taux_musculaire: 'Taux musculaire',
        graisse_viscerale: 'Graisse viscérale',
        eau_corporelle: 'Eau corporelle',
        muscle_squelettique: 'Muscle squelettique',
        masse_musculaire: 'Masse musculaire'
      }
      return labels[metric] || metric
    }

    const getMetricUnit = (metric) => {
      const units = {
        poids: 'kg',
        imc: '',
        graisse_corporelle: '%',
        taux_musculaire: '%',
        graisse_viscerale: '',
        eau_corporelle: '%',
        muscle_squelettique: '%',
        masse_musculaire: 'kg'
      }
      return units[metric] || ''
    }

    const formatDateRange = (startDate, endDate) => {
      if (!startDate || !endDate) return '-'
      const start = new Date(startDate).toLocaleDateString('fr-FR')
      const end = new Date(endDate).toLocaleDateString('fr-FR')
      return `${start} → ${end}`
    }

    const calculateDuration = (startDate, endDate) => {
      if (!startDate || !endDate) return 0
      const start = new Date(startDate)
      const end = new Date(endDate)
      const diff = Math.ceil((end - start) / (1000 * 60 * 60 * 24))
      return diff + 1
    }

    const createGoal = async () => {
      try {
        // Validation des dates
        if (new Date(newGoal.value.endDate) < new Date(newGoal.value.startDate)) {
          alert('La date de fin doit être après la date de début')
          return
        }

        const goalData = {
          id: `goal_${Date.now()}`,
          metric: newGoal.value.metric,
          targetValue: newGoal.value.targetValue,
          startValue: currentValue.value !== '-' ? currentValue.value : null,
          startDate: newGoal.value.startDate,
          endDate: newGoal.value.endDate,
          createdAt: new Date().toISOString(),
          status: 'active'
        }

        const result = await bodyMetricsAPI.addGoal(props.currentUser, goalData)

        if (result.success) {
          emit('goals-updated')
          newGoal.value = {
            metric: '',
            targetValue: null,
            startDate: new Date().toISOString().slice(0, 10),
            endDate: ''
          }
          showForm.value = false
        }
      } catch (error) {
        console.error('Erreur lors de la création de l\'objectif:', error)
        alert('Erreur lors de la création de l\'objectif')
      }
    }

    const deleteGoal = () => {
      showDeleteModal.value = true
    }

    const confirmDelete = async () => {
      try {
        if (!currentGoal.value) return

        const result = await bodyMetricsAPI.deleteGoal(props.currentUser, currentGoal.value.id)

        if (result.success) {
          emit('goals-updated')
          showDeleteModal.value = false
        }
      } catch (error) {
        console.error('Erreur lors de la suppression de l\'objectif:', error)
        alert('Erreur lors de la suppression de l\'objectif')
      }
    }

    const startEdit = () => {
      if (!currentGoal.value) return
      editGoal.value = {
        metric: currentGoal.value.metric,
        targetValue: currentGoal.value.targetValue,
        startDate: currentGoal.value.startDate,
        endDate: currentGoal.value.endDate
      }
      isEditing.value = true
    }

    const cancelEdit = () => {
      isEditing.value = false
      editGoal.value = {
        metric: '',
        targetValue: null,
        startDate: '',
        endDate: ''
      }
    }

    const updateGoal = async () => {
      try {
        // Validation des dates
        if (new Date(editGoal.value.endDate) < new Date(editGoal.value.startDate)) {
          alert('La date de fin doit être après la date de début')
          return
        }

        const goalData = {
          ...currentGoal.value,
          metric: editGoal.value.metric,
          targetValue: editGoal.value.targetValue,
          startDate: editGoal.value.startDate,
          endDate: editGoal.value.endDate,
          updatedAt: new Date().toISOString()
        }

        const result = await bodyMetricsAPI.updateGoal(props.currentUser, currentGoal.value.id, goalData)

        if (result.success) {
          emit('goals-updated')
          isEditing.value = false
          editGoal.value = {
            metric: '',
            targetValue: null,
            startDate: '',
            endDate: ''
          }
        }
      } catch (error) {
        console.error('Erreur lors de la modification de l\'objectif:', error)
        alert('Erreur lors de la modification de l\'objectif')
      }
    }

    const confirmArchive = async () => {
      try {
        if (!currentGoal.value) return

        const result = await bodyMetricsAPI.archiveGoal(props.currentUser, currentGoal.value.id)

        if (result.success) {
          emit('goals-updated')
          showArchiveModal.value = false
        }
      } catch (error) {
        console.error('Erreur lors de l\'archivage de l\'objectif:', error)
        alert('Erreur lors de l\'archivage de l\'objectif')
      }
    }

    const unarchiveGoal = async (goalId) => {
      try {
        const result = await bodyMetricsAPI.unarchiveGoal(props.currentUser, goalId)

        if (result.success) {
          emit('goals-updated')
        }
      } catch (error) {
        console.error('Erreur lors du désarchivage de l\'objectif:', error)
        alert('Erreur lors du désarchivage de l\'objectif')
      }
    }

    const formatDate = (isoDate) => {
      if (!isoDate) return '-'
      return new Date(isoDate).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    }

    const renderChart = () => {
      // Éviter les appels simultanés
      if (isRendering.value) {
        return
      }

      // Vérifications robustes avant de créer le graphique
      if (!isChartReady.value || !chartCanvas.value || !currentGoal.value || !props.userData?.data?.measurements) {
        return
      }

      // Vérifier que l'élément canvas est bien dans le DOM et accessible
      if (!chartCanvas.value || !chartCanvas.value.getContext) {
        console.warn('Canvas non disponible, rendu du graphique différé')
        isChartReady.value = false
        return
      }

      // CRITIQUE: Vérifier que le canvas est visible et a des dimensions
      const canvasRect = chartCanvas.value.getBoundingClientRect()
      if (canvasRect.width === 0 || canvasRect.height === 0) {
        console.warn('Canvas non visible (dimensions nulles), rendu différé')
        // Réessayer quand l'onglet sera visible
        setTimeout(() => {
          if (chartCanvas.value && chartCanvas.value.getBoundingClientRect().width > 0) {
            isRendering.value = false
            renderChart()
          }
        }, 500)
        return
      }

      isRendering.value = true

      // Détruire le graphique existant
      if (chartInstance.value) {
        try {
          chartInstance.value.destroy()
          chartInstance.value = null
        } catch (error) {
          console.error('Erreur lors de la destruction du graphique:', error)
        }
      }

      const measurements = props.userData.data.measurements
      const metric = currentGoal.value.metric

      // Normaliser les dates pour éviter les problèmes de fuseau horaire
      const goalStartDate = currentGoal.value.startDate // Format: "YYYY-MM-DD"
      const goalEndDate = currentGoal.value.endDate // Format: "YYYY-MM-DD"

      // Filtrer les mesures dans la plage de dates
      const rangeMeasurements = measurements.filter(m => {
        // Convertir "DD/MM/YYYY" en "YYYY-MM-DD"
        const measurementDateStr = m.date.split('/').reverse().join('-')
        // Comparaison de chaînes fonctionne bien avec le format ISO (YYYY-MM-DD)
        return measurementDateStr >= goalStartDate && measurementDateStr <= goalEndDate
      }).sort((a, b) => {
        const dateA = new Date(a.date.split('/').reverse().join('-') + 'T' + a.time)
        const dateB = new Date(b.date.split('/').reverse().join('-') + 'T' + b.time)
        return dateA - dateB
      })

      // Si pas de mesures pour cette période, afficher un graphique vide avec message
      const labels = rangeMeasurements.length > 0
        ? rangeMeasurements.map(m => m.date)
        : ['Pas de données']

      const data = rangeMeasurements.length > 0
        ? rangeMeasurements.map(m => m.data[metric]?.value || null)
        : []

      const targetValue = currentGoal.value.targetValue

      // Calculer la trajectoire idéale : de la PREMIÈRE MESURE jusqu'à l'OBJECTIF FINAL
      let startValue = currentGoal.value.startValue
      if (!startValue && rangeMeasurements.length > 0) {
        startValue = rangeMeasurements[0].data[metric]?.value
      }

      const projectedData = []
      if (startValue && rangeMeasurements.length > 0) {
        // Date de la première mesure = point de départ de la trajectoire
        const firstMeasurementDate = new Date(rangeMeasurements[0].date.split('/').reverse().join('-'))
        const goalEnd = new Date(goalEndDate)

        // Nombre de jours entre la première mesure et la fin de l'objectif
        const totalDays = Math.ceil((goalEnd - firstMeasurementDate) / (1000 * 60 * 60 * 24))

        // Changement quotidien idéal (depuis la première mesure)
        const totalChange = targetValue - startValue
        const dailyChange = totalDays > 0 ? totalChange / totalDays : 0

        // Pour chaque mesure, calculer où on devrait être à cette date
        rangeMeasurements.forEach(m => {
          const measurementDate = new Date(m.date.split('/').reverse().join('-'))
          const daysElapsed = Math.ceil((measurementDate - firstMeasurementDate) / (1000 * 60 * 60 * 24))
          const idealValue = startValue + (dailyChange * daysElapsed)
          projectedData.push(Math.round(idealValue * 100) / 100)
        })
      } else if (labels.length > 0) {
        // Si pas de valeur de départ, remplir avec null
        projectedData.push(...Array(labels.length).fill(null))
      }

      try {
        const ctx = chartCanvas.value.getContext('2d')
        if (!ctx) {
          console.error('Impossible d\'obtenir le contexte 2D du canvas')
          return
        }

        // Vérification supplémentaire que le canvas est vraiment accessible
        try {
          ctx.save()
          ctx.restore()
        } catch (error) {
          console.warn('Canvas context non valide, rendu différé')
          setTimeout(() => renderChart(), 500)
          return
        }

        chartInstance.value = new Chart(ctx, {
        type: 'line',
        data: {
          labels: labels,
          datasets: [
            {
              label: getMetricLabel(metric),
              data: data,
              borderColor: '#667eea',
              backgroundColor: 'rgba(102, 126, 234, 0.1)',
              tension: 0.4,
              fill: true
            },
            {
              label: 'Trajectoire idéale',
              data: projectedData,
              borderColor: '#48bb78',
              borderDash: [8, 4],
              borderWidth: 2,
              pointRadius: 0,
              fill: false,
              tension: 0
            },
            {
              label: 'Objectif',
              data: Array(labels.length).fill(targetValue),
              borderColor: '#f56565',
              borderDash: [5, 5],
              borderWidth: 2,
              pointRadius: 0,
              fill: false
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: true,
              position: 'top'
            },
            tooltip: {
              mode: 'index',
              intersect: false
            }
          },
          scales: {
            y: {
              beginAtZero: false,
              title: {
                display: true,
                text: getMetricUnit(metric)
              }
            },
            x: {
              title: {
                display: true,
                text: 'Date'
              },
              ticks: {
                maxRotation: 45,
                minRotation: 45
              }
            }
          }
        }
      })
        isRendering.value = false
      } catch (error) {
        console.error('Erreur lors de la création du graphique Chart.js:', error)
        chartInstance.value = null
        isRendering.value = false
      }
    }

    // Timeout pour debounce
    let renderTimeout = null

    // Un seul watcher combiné pour éviter les appels multiples
    watch([() => currentGoal.value, () => props.userData], async ([newGoal, newUserData]) => {
      // Annuler le timeout précédent si existe
      if (renderTimeout) {
        clearTimeout(renderTimeout)
        renderTimeout = null
      }

      if (newGoal && newUserData) {
        isChartReady.value = false
        isRendering.value = false
        await nextTick()

        // Debounce de 500ms pour éviter les appels multiples
        renderTimeout = setTimeout(() => {
          isChartReady.value = true
          renderChart()
          renderTimeout = null
        }, 500)
      }
    })

    onMounted(async () => {
      await nextTick()
      // Attendre que le DOM soit complètement prêt
      setTimeout(() => {
        if (currentGoal.value && chartCanvas.value) {
          isChartReady.value = true
          renderChart()
        }
      }, 300)
    })

    onUnmounted(() => {
      // Nettoyer le timeout en cours
      if (renderTimeout) {
        clearTimeout(renderTimeout)
        renderTimeout = null
      }

      // Détruire l'instance du graphique
      if (chartInstance.value) {
        try {
          chartInstance.value.destroy()
          chartInstance.value = null
        } catch (error) {
          console.error('Erreur lors de la destruction du graphique:', error)
        }
      }
    })

    return {
      chartCanvas,
      newGoal,
      editGoal,
      currentGoal,
      archivedGoals,
      currentValue,
      startValueDisplay,
      difference,
      diffClass,
      progressPercent,
      isGoalCompleted,
      showDeleteModal,
      showArchiveModal,
      showForm,
      showHistory,
      isChartReady,
      isEditing,
      getMetricLabel,
      getMetricUnit,
      formatDateRange,
      formatDate,
      calculateDuration,
      createGoal,
      deleteGoal,
      confirmDelete,
      confirmArchive,
      unarchiveGoal,
      startEdit,
      cancelEdit,
      updateGoal
    }
  }
}
</script>

<style scoped>
.goal-view {
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

/* Carte d'objectif */
.goal-card {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
}

.goal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
}

.goal-header h2 {
  color: #2c3e50;
  font-size: 1.5rem;
  margin: 0;
}

.goal-actions {
  display: flex;
  gap: 0.5rem;
}

.edit-goal-btn {
  background: #667eea;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.edit-goal-btn:hover {
  background: #5a67d8;
  transform: scale(1.05);
}

.delete-goal-btn {
  background: #f56565;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.delete-goal-btn:hover {
  background: #e53e3e;
  transform: scale(1.05);
}

.cancel-edit-btn {
  background: #a0aec0;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  color: white;
}

.cancel-edit-btn:hover {
  background: #718096;
  transform: scale(1.05);
}

.form-section.editing {
  border: 2px solid #667eea;
}

.form-section.editing .goal-header {
  margin-bottom: 1.5rem;
}

.form-actions {
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
}

.cancel-btn {
  background: #e2e8f0;
  color: #2c3e50;
  border: none;
  border-radius: 8px;
  padding: 1rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.cancel-btn:hover {
  background: #cbd5e0;
}

.goal-info {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
}

.goal-metric, .goal-target, .goal-current, .goal-diff, .goal-period, .goal-days, .goal-start {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.label {
  color: #6c757d;
  font-size: 0.9rem;
  font-weight: 500;
}

.value {
  color: #2c3e50;
  font-size: 1.2rem;
  font-weight: 600;
}

.value.positive {
  color: #48bb78;
}

.value.negative {
  color: #f56565;
}

.value.neutral {
  color: #667eea;
}

/* Barre de progression */
.progress-section {
  margin-top: 1.5rem;
}

.progress-bar-container {
  width: 100%;
  height: 30px;
  background: #e2e8f0;
  border-radius: 15px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  transition: width 0.5s ease;
  border-radius: 15px;
}

.progress-text {
  text-align: center;
  margin-top: 0.5rem;
  color: #667eea;
  font-weight: 600;
  font-size: 1.1rem;
}

/* Section graphique */
.chart-section {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
}

.chart-section h2 {
  color: #2c3e50;
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
}

.chart-wrapper {
  position: relative;
  min-height: 400px;
}

.chart-container {
  height: 400px;
  position: relative;
}

.chart-loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: white;
  color: #6c757d;
  font-size: 1.1rem;
  z-index: 10;
}

/* Formulaire */
.form-section {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
}

.form-section h2 {
  color: #2c3e50;
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
}

.goal-form {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-group label {
  color: #4a5568;
  font-weight: 500;
}

.form-group input,
.form-group select {
  padding: 0.75rem;
  border: 1px solid #cbd5e0;
  border-radius: 8px;
  font-size: 1rem;
  transition: border-color 0.3s ease;
}

.form-group input:focus,
.form-group select:focus {
  outline: none;
  border-color: #667eea;
}

.submit-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 1rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.submit-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

/* État vide */
.empty-state {
  background: white;
  border-radius: 12px;
  padding: 4rem 2rem;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  text-align: center;
}

.empty-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
}

.empty-state p {
  color: #6c757d;
  font-size: 1.2rem;
  margin-bottom: 2rem;
}

.create-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 1rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: transform 0.3s ease;
}

.create-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  border-radius: 12px;
  padding: 2rem;
  max-width: 400px;
  width: 90%;
}

.modal-content h3 {
  color: #2c3e50;
  margin-bottom: 1rem;
}

.modal-content p {
  color: #6c757d;
  margin-bottom: 1.5rem;
}

.modal-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
}

.btn-danger {
  background: #f56565;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-danger:hover {
  background: #e53e3e;
}

.btn-cancel {
  background: #e2e8f0;
  color: #2c3e50;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-cancel:hover {
  background: #cbd5e0;
}

/* ========== STYLES OBJECTIF ATTEINT ========== */

/* Carte objectif atteint */
.goal-card.goal-completed {
  border: 3px solid #48bb78;
  background: linear-gradient(135deg, #f0fff4 0%, #c6f6d5 100%);
  position: relative;
  overflow: hidden;
}

/* Bannière de célébration */
.celebration-banner {
  background: linear-gradient(135deg, #ffd700 0%, #ffb347 100%);
  margin: -2rem -2rem 1.5rem -2rem;
  padding: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  position: relative;
  overflow: hidden;
}

.celebration-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  z-index: 2;
}

.celebration-text {
  font-size: 1.5rem;
  font-weight: 800;
  color: #2c3e50;
  text-shadow: 2px 2px 4px rgba(255,255,255,0.5);
  animation: pulse 1.5s ease-in-out infinite;
}

.celebration-emoji {
  font-size: 2rem;
  animation: bounce 1s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}

/* Confettis CSS */
.confetti {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  pointer-events: none;
  overflow: hidden;
}

.confetti::before,
.confetti::after {
  content: "🎊 ⭐ 🎉 ✨ 🏆 🎊 ⭐ 🎉 ✨ 🏆";
  position: absolute;
  font-size: 1.5rem;
  animation: confetti-fall 3s linear infinite;
  white-space: nowrap;
}

.confetti::before {
  top: -50px;
  left: 0;
  animation-delay: 0s;
}

.confetti::after {
  top: -50px;
  right: 0;
  animation-delay: 1.5s;
}

@keyframes confetti-fall {
  0% {
    transform: translateY(-100%) rotate(0deg);
    opacity: 1;
  }
  100% {
    transform: translateY(200%) rotate(720deg);
    opacity: 0;
  }
}

/* Barre de progression complétée */
.progress-bar-container.completed {
  background: #c6f6d5;
}

.progress-bar.completed {
  background: linear-gradient(135deg, #ffd700 0%, #ffb347 50%, #ffd700 100%);
  background-size: 200% 100%;
  animation: shimmer 2s linear infinite;
}

@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

.progress-text.completed {
  color: #2f855a;
  font-size: 1.2rem;
  font-weight: 700;
}

/* Bouton d'archivage */
.archive-goal-btn {
  background: #9f7aea;
  border: none;
  border-radius: 8px;
  padding: 0.5rem 1rem;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.archive-goal-btn:hover {
  background: #805ad5;
  transform: scale(1.05);
}

/* Prompt d'archivage */
.archive-prompt {
  margin-top: 1.5rem;
  text-align: center;
}

.archive-celebration-btn {
  background: linear-gradient(135deg, #ffd700 0%, #ff8c00 100%);
  color: #2c3e50;
  border: none;
  border-radius: 12px;
  padding: 1rem 2rem;
  font-size: 1.2rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(255, 215, 0, 0.4);
  animation: glow 2s ease-in-out infinite;
}

.archive-celebration-btn:hover {
  transform: translateY(-3px) scale(1.02);
  box-shadow: 0 6px 20px rgba(255, 215, 0, 0.6);
}

@keyframes glow {
  0%, 100% { box-shadow: 0 4px 15px rgba(255, 215, 0, 0.4); }
  50% { box-shadow: 0 4px 25px rgba(255, 215, 0, 0.8); }
}

/* Modal d'archivage */
.archive-modal .archive-modal-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.archive-modal .archive-emoji {
  font-size: 2rem;
}

.archive-modal h3 {
  margin: 0;
}

.btn-archive {
  background: linear-gradient(135deg, #9f7aea 0%, #805ad5 100%);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
}

.btn-archive:hover {
  background: linear-gradient(135deg, #805ad5 0%, #6b46c1 100%);
  transform: translateY(-2px);
}

/* ========== SECTION HISTORIQUE ========== */

.history-section {
  background: white;
  border-radius: 12px;
  padding: 1.5rem 2rem;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  margin-top: 1rem;
}

.history-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  user-select: none;
}

.history-header h2 {
  color: #2c3e50;
  font-size: 1.3rem;
  margin: 0;
}

.toggle-icon {
  font-size: 1rem;
  color: #6c757d;
  transition: transform 0.3s ease;
}

.history-header:hover .toggle-icon {
  color: #667eea;
}

.history-list {
  margin-top: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.history-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  border-left: 4px solid #48bb78;
}

.history-card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.history-badge {
  background: #c6f6d5;
  color: #2f855a;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
}

.history-date {
  color: #6c757d;
  font-size: 0.85rem;
}

.history-card-body {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.history-metric {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.history-metric strong {
  color: #2c3e50;
}

.history-metric span {
  color: #6c757d;
}

.history-period {
  color: #6c757d;
  font-size: 0.9rem;
}

.history-card-actions {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e2e8f0;
}

.unarchive-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
}

.unarchive-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

/* Responsive */
@media (max-width: 768px) {
  .goal-info {
    grid-template-columns: 1fr;
  }

  .chart-container {
    height: 300px;
  }

  .form-row {
    grid-template-columns: 1fr;
  }
}
</style>
